import { useEffect, useMemo, useState, type ReactNode, type Ref } from 'react';

import { MarkedContext, marksOf, type FieldProblem } from './fieldProblem';

/**
 * Wraps a settings form so a refusal about a field is drawn on that field — see
 * `fieldProblem.ts` for why and how the field is found.
 *
 * Several fields at once when the problem lists several: each turns red with its own reason
 * under it, the first is scrolled to and focused, and editing one clears that one alone. The
 * page hears about it (`onEdit`) only when the last of them has been edited, because until
 * then the banner counting them is still true.
 */
export function FieldErrors({
  problem,
  prefix = '',
  onEdit,
  root,
  children,
}: {
  problem: FieldProblem | null;
  /** Put in front of the field's name, for a page whose ids carry one — the platform screen. */
  prefix?: string;
  /** Somebody changed every marked field: whatever was said about them no longer holds. */
  onEdit?: () => void;
  /**
   * An element around the children for `checkForm` to read every control from, drawn with
   * `display: contents` so it changes nothing about the layout. Only when asked for.
   */
  root?: Ref<HTMLDivElement>;
  children?: ReactNode;
}) {
  // Which marks have been edited away, for the problem they belong to. Kept beside the
  // problem rather than reset in an effect, so a new problem starts with nothing cleared.
  const [cleared, setCleared] = useState<{ problem: FieldProblem | null; ids: readonly string[] }>({
    problem: null,
    ids: [],
  });

  const marks = useMemo(() => {
    const all = marksOf(problem, prefix);
    const gone = cleared.problem === problem ? cleared.ids : [];

    return all.filter((mark) => !gone.includes(mark.id));
  }, [problem, prefix, cleared]);

  const marked =
    marks.length === 0
      ? null
      : {
          marks,
          onEdit: (id: string) => {
            const remaining = marks.filter((mark) => mark.id !== id);

            setCleared((current) => ({
              problem,
              ids: [...(current.problem === problem ? current.ids : []), id],
            }));

            if (remaining.length === 0) onEdit?.();
          },
        };

  // Keyed on the problem, not on the id, so a second Save refused over the same field goes
  // back to it — somebody who scrolled away to look at something else is taken back.
  useEffect(() => {
    const first = marksOf(problem, prefix)[0];

    if (first === undefined) return;

    const control = document.getElementById(first.id);

    if (control === null) return;

    // Centred rather than merely into view: the save bar is pinned to the bottom of the
    // page, and a field scrolled just into view is a field sitting underneath it.
    control.scrollIntoView({ block: 'center', behavior: 'smooth' });
    control.focus({ preventScroll: true });
  }, [problem, prefix]);

  return (
    <MarkedContext value={marked}>
      {root === undefined ? (
        children
      ) : (
        <div ref={root} className="contents">
          {children}
        </div>
      )}
    </MarkedContext>
  );
}
